import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { TextField } from '@mui/material';
import { CategoryMultiSelect } from './shared/CategoryMultiSelect';
import { StepperRouting } from './shared/StepperRouting';
import { CampaignTypeCard } from './shared/CampaignTypeCard';
import { BriefingService } from '../service';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { ICreatorTypes } from '../../../../../model/briefing/step1/ICreatorTypes';
import { iPlatformIcons } from '../../manage/calendar/dto/IManageCalendar';
import { useTranslation } from 'react-i18next';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { IContentCreator } from '../../../../../model/briefing/step5/IContentCreator';
import { oneStepUp } from '../../../../../shared/functions/Functions';
import { CampaignCategory } from '../../../../../model/campaign/CampaignCategory';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import CardLayout from '../../../../../shared/components/card/CardLayout';

export const StepFive = () => {
    const { country, gender, campaignCategory } = useSelector((states: IAllStates) => states.enums);
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [isBriefingComplete, setIsBriefingComplete] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [openWarningModal, setOpenWarningModal] = useState(false);
    const [isNextClicked, setIsNextClicked] = useState(false);

    const [fifthStep, setFifthStep] = useState<IContentCreator>({
        id: 0,
        gender: '',
        ageMin: 0,
        ageMax: 0,
        countriesArray: [],
        status: '',
    });

    const noCategorySelected = !fifthStep.categories?.some(item => item.isSelected);

    useEffect(() => {
        BriefingService.getBriefingStepFive(id).then(response => {
            const filtered = response?.categories?.map((category: CampaignCategory) => {
                return {
                    ...category,
                    path: `/icons/categories/${category?.name}.svg`,
                };
            });
            setFifthStep({ ...response, categories: filtered });
            setIsBriefingComplete(response?.isBriefingComplete);
        });
    }, [campaignCategory]);

    const updateStepFive = (modalYes: boolean) => {
        setIsSubmitted(true);
        const hasInfluencerData = !fifthStep?.ageMin || !fifthStep?.ageMax || !fifthStep?.gender;
        const hasError =
            !fifthStep?.countriesArray?.length || fifthStep.categories?.every(category => !category.isSelected);
        if (hasError) {
            return;
        }
        if (hasInfluencerData && !modalYes) {
            setOpenWarningModal(true);
            return;
        }
        const data = { ...fifthStep, categories: fifthStep.categories?.filter(category => category.isSelected) };
        setIsNextClicked(true);
        BriefingService.updateStepFive(data, id)
            .then(response => {
                if (response) {
                    oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                    SuccessToast(`${t('general.alert.success_updated_campaign_step')}`);
                }
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                setIsNextClicked(false);
            });
    };
    const selectCategoryHandler = (id: number | undefined) => {
        const copy = [...(fifthStep?.categories as iPlatformIcons[])];
        const indexToReplace = copy?.findIndex(item => item?.id === id);
        copy[indexToReplace] = {
            ...copy[indexToReplace],
            isSelected: !copy[indexToReplace]?.isSelected,
        };

        setFifthStep(prev => ({ ...prev, categories: copy }));
    };

    const toggleCategoriesSelection = () => {
        setFifthStep(prevState => {
            const noCategorySelected = !prevState.categories?.some(category => category.isSelected);

            return {
                ...prevState,
                categories: prevState.categories?.map(category => ({
                    ...category,
                    isSelected: noCategorySelected,
                })),
            };
        });
    };
    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setFifthStep((prev: IContentCreator) => ({ ...prev, [paramKey]: paramValue }));
    };

    const changeAutocompleteHandler = (paramKey: string, paramValue: string | string[] | number) => {
        setFifthStep(prev => ({ ...prev, [paramKey]: paramValue === '' ? null : paramValue }));
    };

    const handleChangeAgeRange = (e: any) => {
        setFifthStep((prev: IContentCreator) => ({ ...prev, [e.target.name]: +e.target.value }));
    };

    return (
        <Row className="mb-5 pb-5">
            <ConfirmationModal
                title={t('general.confirm')}
                description={t('brand.campaign.briefing.step2.areYouSure')}
                action={() => updateStepFive(true)}
                show={openWarningModal}
                closeModal={() => setOpenWarningModal(false)}
            />
            <Col md={8}>
                {!isBriefingComplete ? (
                    <div className="mb-4">
                        <ProgressBarCampaign title={t('brand.campaign.briefing.step5.creator_matching')} percent={45} />
                    </div>
                ) : null}
                <div className="mb-3">
                    <h5 className="font-weight-bold">{t('brand.campaign.briefing.step5.creators_type')}</h5>
                </div>

                <CardLayout>
                    <div className="row flex-column flex-md-row align-items-start mb-4">
                        <div className="col-md-4">
                            <h6 className="font-weight-bold">{t('brand.campaign.briefing.step1.creator_type')}</h6>
                            <CampaignTypeCard item={fifthStep?.creatorType as ICreatorTypes} />
                        </div>
                        <div className="col-md-8 d-flex flex-column">
                            <div>
                                <h6 className="mb-3 font-weight-bold">
                                    {t('brand.campaign.briefing.step5.influencer_location')}
                                </h6>
                                <CustomAutocomplete
                                    dataArray={country}
                                    isWithImage={true}
                                    inputLabel="country"
                                    defaultArray={fifthStep?.countriesArray}
                                    isMultiple={true}
                                    hasError={isSubmitted && !fifthStep?.countriesArray?.length}
                                    photoLabel="countryFlag"
                                    queryFilterKey="countriesArray"
                                    inputLabelTranslation={t('brand.campaign.book.offer.filters.location')}
                                    handleChangeParams={multipleSelectHandler}
                                />
                            </div>
                            <div className="d-flex justify-content-between mt-3 flex-column flex-md-row">
                                <div className="w-md-50">
                                    <h6 className="mt-3 font-weight-bold">
                                        {t('brand.campaign.briefing.step5.influencer_gender')}
                                    </h6>
                                    <CustomAutocomplete
                                        dataArray={gender}
                                        isMultiple={false}
                                        defaultArray={fifthStep?.gender}
                                        isWithImage={false}
                                        inputLabel="gender"
                                        queryFilterKey="gender"
                                        inputLabelTranslation="Gender"
                                        handleChangeParams={changeAutocompleteHandler}
                                    />
                                </div>

                                <div className="w-md-50 ml-md-3">
                                    <h6 className="mt-3 font-weight-bold">
                                        {t('brand.campaign.briefing.step5.age_range')}
                                    </h6>
                                    <div className="d-flex align-items-center">
                                        <TextField
                                            focused={!!fifthStep?.ageMin}
                                            onChange={handleChangeAgeRange}
                                            label="Min"
                                            value={fifthStep?.ageMin || ''}
                                            variant="outlined"
                                            name="ageMin"
                                            type="number"
                                            size="small"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyPress={event =>
                                                (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                            }
                                        />
                                        <span className="mx-3">-</span>
                                        <TextField
                                            focused={!!fifthStep?.ageMax}
                                            onChange={handleChangeAgeRange}
                                            label="Max"
                                            value={fifthStep?.ageMax || ''}
                                            variant="outlined"
                                            name="ageMax"
                                            type="number"
                                            size="small"
                                            InputProps={{ inputProps: { min: 0 } }}
                                            onKeyPress={event =>
                                                (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="w-md-auto w-65">
                            <h4 className="font-weight-bold mb-0">{t('brand.campaign.briefing.step5.category')}</h4>
                            <span className="text-muted">
                                {t('brand.campaign.briefing.step5.category_description')}
                            </span>
                        </div>
                        <Button onClick={toggleCategoriesSelection} variant="outline-primary">
                            {noCategorySelected ? t('general.selectAll') : t('general.deselectAll')}
                        </Button>
                    </div>
                    <div className="d-flex flex-wrap my-3">
                        {fifthStep?.categories?.map(category => (
                            <CategoryMultiSelect
                                item={category as any}
                                name={t(`general.category.${category.name}`)}
                                key={category?.name}
                                error={isSubmitted && fifthStep.categories?.every(cat => !cat.isSelected)}
                                isActive={category?.isSelected as boolean}
                                clickHandler={() => selectCategoryHandler(category?.id)}
                            />
                        ))}
                    </div>
                    <StepperRouting
                        submitStepHandler={() => updateStepFive(false)}
                        isNextDisabled={isNextClicked}
                        status={fifthStep?.status}
                    />
                </CardLayout>
            </Col>
            <Col md={4} className="pr-md-4">
                <CampaignOverviewCard />
            </Col>
        </Row>
    );
};
